import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import urls from "../../urls";
import openingHoursNL from "../../data/company/opening_hours.json";
import openingHoursEN from "../../data/company/opening_hours_en.json";
import i18n from "../../translations/i18n";
import { LANGUAGE_NL } from "../../constants/LanguageConstants";
import FooterBottom from "../../components/footer/FooterBottom";
import { formatNameToURL } from "../../helpers/formatters";
import { orderBy } from "lodash";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { styles } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import BannerTitle from "../../components/core/BannerTitle";
import { FooterNavItems } from "../../configs/NavConfigs";
import { BackgroundElement } from "../../styles/SharedStyles";
import { useProducts } from "../../components/hooks/firestore/useProducts";

const Footer = () => {
  const { t } = useTranslation();
  const [openingHoursData, setOpeningHoursData] = useState(openingHoursNL);
  const { data: products } = useProducts();
  const [popularProducts, setPopularProducts] = useState([]);
  const firstColumnProducts = popularProducts.slice(0, 8);
  const secondColumnProducts = popularProducts.slice(8);

  useEffect(() => {
    // Calculate most used categories
    const filteredProducts = products.filter((p) => !!p.purchased);
    const mostOrderedProducts = orderBy(
      filteredProducts,
      ["purchased"],
      ["desc"],
    );
    setPopularProducts(mostOrderedProducts.slice(0, 16));
  }, []);

  useEffect(() => {
    setOpeningHoursData(
      i18n.language === LANGUAGE_NL ? openingHoursNL : openingHoursEN,
    );
  }, [i18n.language]);

  return (
    <StyledFooter>
      <StyledContainer>
        <BannerTitle dark>{t("footer_title")}</BannerTitle>
        <GridContainer>
          <Column>
            <h3>{t("products")}</h3>
            <FooterList>
              {firstColumnProducts.length > 0 &&
                firstColumnProducts.map((product, key) => (
                  <li key={key}>
                    <Link to={urls.product + formatNameToURL(product.url)}>
                      {product.name}
                    </Link>
                  </li>
                ))}
              {secondColumnProducts.length > 0 &&
                secondColumnProducts.map((product, key) => (
                  <li key={key} className={"mobile-visible"}>
                    <Link to={urls.product + formatNameToURL(product.url)}>
                      {product.name}
                    </Link>
                  </li>
                ))}
            </FooterList>
          </Column>
          <Column className={"mobile-hide"}>
            <h3>{t("products")}</h3>
            <FooterList>
              {secondColumnProducts.length > 0 &&
                secondColumnProducts.map((product, key) => (
                  <li key={key}>
                    <Link to={urls.product + formatNameToURL(product.url)}>
                      {product.name}
                    </Link>
                  </li>
                ))}
            </FooterList>
          </Column>
          <Column>
            <h3>{t("customer_service")}</h3>
            <FooterList>
              {FooterNavItems.map((item, index) => (
                <li key={index}>
                  <Link to={item.url}>{t(item.label)}</Link>
                </li>
              ))}
            </FooterList>
          </Column>
          <Column>
            <h3>{t("opening_hours")}</h3>
            <FooterList>
              {openingHoursData.map((item) => (
                <li key={item.id}>
                  <span>
                    {item.title_short}
                    {" - "}
                    {item.value}
                  </span>
                </li>
              ))}
            </FooterList>
            <HalalLogo
              src={"/assets/img/icons/fully-halal-light.svg"}
              alt={"100% Halal"}
              title={"Halal"}
            />
            <HalalLogo
              src={"/assets/img/certificates/eu-organic-logo.png"}
              alt={"Biologisch"}
              title={"Biologisch"}
            />
          </Column>
        </GridContainer>
      </StyledContainer>
      <FooterBottom />
      <RedSpiceBgElement
        src={"/assets/img/styling/spice-splash-Red.webp"}
        alt={""}
      />
      <OrangeSpiceBgElement
        src={"/assets/img/styling/spice-splash-orange.webp"}
        alt={""}
      />
      {/*<LogoBgElement src={"/assets/img/logo/logo-liggend.png"} alt={""} />*/}
    </StyledFooter>
  );
};

const StyledFooter = styled.footer`
  background:
    linear-gradient(transparent, 90%, #000000),
    url("/assets/img/bg/background-dark-1.webp") no-repeat center center / cover;
  color: #fff;
  position: relative;
  isolation: isolate;
  overflow: clip;
`;

const StyledContainer = styled(Container)`
  padding-block: ${styles.banner_padding_mobile};
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @media ${device.lg} {
    padding-block: ${styles.banner_padding_desktop};
  }
`;

const GridContainer = styled.div`
  display: grid;
  gap: 3rem;

  @media only screen and ${device.sm} {
    grid-template-columns: repeat(2, 1fr);
    gap: 4rem 8rem;
  }

  @media only screen and ${device.lg} {
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;

  h3 {
    font-family: "Krusyida Script", cursive;
    color: white;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  .mobile-visible {
    display: block;
    @media ${device.sm} {
      display: none;
    }
  }

  &.mobile-hide {
    display: none;
    @media ${device.sm} {
      display: block;
    }
  }
`;

const FooterList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 9px;

  a,
  span,
  p {
    font-size: 14px;
    font-weight: 500;
    color: white;
    opacity: 0.9;
    margin: 0;
  }

  a {
    &:hover {
      color: white;
      opacity: 0.5;
    }
  }
`;

const HalalLogo = styled.img`
  margin-top: 20px;
  width: 56px;
  height: auto;
  opacity: 0.9;
`;

const LogoBgElement = styled.img`
  position: absolute;
  opacity: 0.3;
  z-index: -1;

  left: -10%;
  top: 0;
  bottom: 0;
  margin-block: auto;
  height: 120vw;

  @media ${device.sm} {
    left: -40px;
    height: 75vw;
  }

  @media ${device.lg} {
    left: 0;
    height: 70%;
  }
`;

const RedSpiceBgElement = styled(BackgroundElement)`
  opacity: 0.2;
  transform: scaleY(-1) rotate(180deg) translateX(65%);
  top: 0;
  bottom: 0;
  left: 0;
  margin: auto;

  @media ${device.md} {
    opacity: 0.3;
  }

  @media ${device.lg} {
    transform: scaleY(-1) rotate(180deg) translateX(65%);
  }
`;

const OrangeSpiceBgElement = styled(BackgroundElement)`
  opacity: 0.2;
  transform: scaleX(-1) translateX(-45%) translateY(-30%);
  top: 0;
  right: 0;
  margin: auto;

  @media ${device.md} {
    opacity: 0.3;
  }

  @media ${device.lg} {
    transform: scaleX(-1) translateX(-45%) translateY(-30%);
  }
`;

export default Footer;
